// src/stats/format.ts

export const METRIC_LABELS: Record<string, string> = {
  mrr: 'MRR',
  active_subscriptions: 'Abonnés actifs',
  active_trials: 'Essais actifs',
  revenue_net: 'Revenus nets',
  churn_rate: 'Churn',
  trial_conversion_rate: 'Conv. trial→payant',
  new_downloads: 'Téléchargements',
  rating_avg: 'Note moyenne',
  rating_count: 'Nb avis',
}

type MetricType = 'currency' | 'percent' | 'rating' | 'integer'

const METRIC_TYPES: Record<string, MetricType> = {
  mrr: 'currency',
  revenue_net: 'currency',
  churn_rate: 'percent',
  trial_conversion_rate: 'percent',
  rating_avg: 'rating',
  active_subscriptions: 'integer',
  active_trials: 'integer',
  new_downloads: 'integer',
  rating_count: 'integer',
}

/** Formate un entier avec séparateur de milliers espace normal (fr-FR). */
function formatInteger(value: number): string {
  return new Intl.NumberFormat('fr-FR', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  })
    .format(value)
    .replace(/ /g, ' ')
}

/**
 * Formate une valeur métrique pour l'affichage.
 */
export function formatMetricValue(metric: string, value: number): string {
  const type = METRIC_TYPES[metric]

  if (type === 'currency') {
    return formatInteger(value) + ' €'
  }

  if (type === 'percent') {
    return (value * 100).toFixed(2) + ' %'
  }

  if (type === 'rating') {
    return value.toFixed(1) + ' ★'
  }

  if (type === 'integer') {
    return formatInteger(value)
  }

  return String(value)
}

/**
 * Calcule et formate le delta en pourcentage entre current et previous.
 * Retourne null si previous est null ou 0 (division par zéro).
 */
export function formatDelta(current: number, previous: number | null): string | null {
  if (previous === null || previous === 0) return null
  const pct = ((current - previous) / Math.abs(previous)) * 100
  const sign = pct >= 0 ? '+' : ''
  return `${sign}${pct.toFixed(1)} %`
}

/**
 * Indique si un delta est positif (hausse) ou négatif (baisse).
 * Utile pour la couleur du badge.
 * Pour le churn_rate, une hausse est mauvaise (inverser la sémantique).
 */
export function isDeltaPositive(metric: string, current: number, previous: number): boolean {
  const isHigherBetter = metric !== 'churn_rate'
  const isHigher = current > previous
  return isHigherBetter ? isHigher : !isHigher
}
